import { FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { SelectField } from "@/components/SelectField/SelectField";

import {
	CreateProduct,
	CreateProductSchema,
	ProductType,
	PRODUCT_TYPE_LABELS,
} from "../schema/productsServices";

interface AddProductModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreateWithAi: (data: CreateProduct) => void;
	onCreateManual: (data: CreateProduct) => void;
}

export const AddProductModal: FunctionComponent<AddProductModalProps> = ({
	isOpen,
	onClose,
	onCreateWithAi,
	onCreateManual,
}) => {
	const [selectedType, setSelectedType] = useState<ProductType>(ProductType.PRODUCT);

	const {
		register,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<CreateProduct>({
		defaultValues: {
			name: "",
			type: ProductType.PRODUCT,
			description: "",
		},
		resolver: zodResolver(CreateProductSchema),
	});

	useEffect(() => {
		setValue("type", selectedType);
	}, [selectedType, setValue]);

	useEffect(() => {
		if (isOpen) {
			reset({ name: "", type: ProductType.PRODUCT, description: "" });
			setSelectedType(ProductType.PRODUCT);
		}
	}, [isOpen, reset]);

	const onSubmitWithAi = (data: CreateProduct) => {
		onCreateWithAi(data);
		onClose();
	};

	const onSubmitManual = (data: CreateProduct) => {
		onCreateManual(data);
		onClose();
	};

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSubmit(onSubmitWithAi)}
			handleSecondSave={handleSubmit(onSubmitManual)}
			isOpened={isOpen}
			secondSubmitButtonText="Create without AI"
			submitButtonText="Create"
			title="Add Product / Service"
		>
			<div className="space-y-4">
				{/* Name */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="product-name">
						Name
					</label>
					<input
						className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
						id="product-name"
						placeholder="e.g. Developer Platform"
						type="text"
						{...register("name")}
					/>
					{errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
				</div>

				{/* Type */}
				<div>
					<SelectField
						handleSelect={(value) => setSelectedType(value as ProductType)}
						label="Type"
						options={Object.values(ProductType).map((type) => ({
							value: type,
							title: PRODUCT_TYPE_LABELS[type],
						}))}
						selectedOption={selectedType}
					/>
				</div>

				{/* Description */}
				<div>
					<label
						className="block text-sm font-medium text-gray-700 mb-1"
						htmlFor="product-description"
					>
						Description
					</label>
					<textarea
						className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
						id="product-description"
						placeholder="Describe the product or service..."
						{...register("description")}
					/>
					{errors.description && (
						<p className="mt-1 text-xs text-red-500">{errors.description.message}</p>
					)}
				</div>
			</div>
		</Modal>
	);
};
